import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Gift, Star, Zap, User, ShieldCheck, Coffee, Minus, Plus, X, HistoryIcon, Users } from "lucide-react";
import { toast } from "sonner";
import { superApi } from "../lib/admin";

/**
 * Super-admin Customer Boost panel — search a customer by phone, view every
 * café loyalty card, and grant free coffees / adjust stamps / grant Beans
 * with one tap. Every action writes to /super/audit which is also viewable
 * inline for reversibility.
 */
const CustomerBoost = () => {
    const [query, setQuery] = useState("");
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [audit, setAudit] = useState([]);
    const [showAudit, setShowAudit] = useState(false);

    const search = async () => {
        const clean = query.replace(/\D/g, "");
        if (clean.length < 6) {
            toast.error("Enter a valid phone number");
            return;
        }
        setLoading(true);
        try {
            const [p, a] = await Promise.all([
                superApi.get(`/super/customers/${clean}`),
                superApi.get(`/super/audit?phone=${clean}&limit=25`),
            ]);
            setProfile(p.data);
            setAudit(a.data.actions || []);
            if (!p.data.cafes.length) {
                toast.info("No café memberships found for this phone");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Lookup failed");
        } finally {
            setLoading(false);
        }
    };

    const refresh = async () => {
        if (!profile?.phone) return;
        const [p, a] = await Promise.all([
            superApi.get(`/super/customers/${profile.phone}`),
            superApi.get(`/super/audit?phone=${profile.phone}&limit=25`),
        ]);
        setProfile(p.data);
        setAudit(a.data.actions || []);
    };

    const grantFreeCoffee = async (slug, count) => {
        const reason = window.prompt("Reason (visible in audit log)", "Promotional gift") || "";
        if (reason === null) return;
        try {
            await superApi.post(`/super/customers/${profile.phone}/grant-free-coffee`, {
                slug, count, reason,
            });
            toast.success(`+${count} free coffee${count > 1 ? "s" : ""}`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const adjustStamps = async (slug, delta) => {
        try {
            await superApi.post(`/super/customers/${profile.phone}/loyalty-adjust`, {
                slug, stamps_delta: delta, free_coffees_delta: 0, reason: "Manual stamp adjustment",
            });
            toast.success(delta > 0 ? "+1 stamp" : "-1 stamp");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const grantBeans = async (slug) => {
        const raw = window.prompt("Beans to grant (negative to claw back)", "100");
        if (!raw) return;
        const amount = parseInt(raw, 10);
        if (Number.isNaN(amount) || amount === 0) return;
        const reason = window.prompt("Reason (visible in audit log)", "Promotional bonus") || "";
        if (reason === null) return;
        try {
            await superApi.post(`/super/customers/${profile.phone}/beans-grant`, {
                slug, amount, reason,
            });
            toast.success(amount > 0 ? `+${amount} Beans` : `${amount} Beans`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <section className="mt-14" data-testid="customer-boost">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                Customer boost
            </div>
            <h2 className="mt-2 font-display font-black text-3xl">Look up. Reward. Fix.</h2>
            <p className="mt-1 text-sm text-[#8C7A72]">
                Grant free coffees, adjust stamps or Beans on any customer's card. Every action is
                audit-logged and reversible.
            </p>

            <BulkBoost />

            {/* Search bar */}
            <div className="mt-6 flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-white border-2 border-[#E8DCCB] focus-within:border-[#FF4F18] rounded-2xl px-4 py-3 transition-colors">
                    <Search className="h-4 w-4 text-[#8C7A72]" strokeWidth={2.5} />
                    <input
                        data-testid="boost-phone-input"
                        type="tel"
                        placeholder="Customer phone e.g. 0499999999"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && search()}
                        className="flex-1 outline-none bg-transparent font-mono text-base tracking-wide"
                    />
                </div>
                <button
                    onClick={search}
                    disabled={loading}
                    data-testid="boost-search-btn"
                    className="px-6 py-3 rounded-2xl bg-[#2C1A12] text-white font-display font-bold disabled:opacity-60 active:scale-[0.98]"
                >
                    {loading ? "Searching…" : "Look up"}
                </button>
            </div>

            {/* Profile */}
            <AnimatePresence>
                {profile && (
                    <motion.div
                        key={profile.phone}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-6 rounded-3xl bg-white border border-[#E8DCCB] p-6"
                        data-testid="boost-profile"
                    >
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                                <div className="h-12 w-12 rounded-2xl bg-[#FF4F18] flex items-center justify-center text-white">
                                    <User className="h-6 w-6" strokeWidth={2.5} />
                                </div>
                                <div>
                                    <div className="font-display font-black text-xl">
                                        {profile.name || "Anonymous"}
                                    </div>
                                    <div className="text-xs text-[#8C7A72] font-mono">
                                        {profile.phone}
                                        {profile.verified && (
                                            <span className="ml-2 inline-flex items-center gap-1 text-[#0E6B4C] font-bold">
                                                <ShieldCheck className="h-3 w-3" strokeWidth={2.5} /> Verified
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowAudit((s) => !s)}
                                data-testid="boost-audit-toggle"
                                className="text-xs uppercase tracking-wider font-bold text-[#8C7A72] hover:text-[#2C1A12] flex items-center gap-1"
                            >
                                <HistoryIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
                                {showAudit ? "Hide" : "Show"} history ({audit.length})
                            </button>
                        </div>

                        {/* Audit log */}
                        {showAudit && (
                            <div className="mt-4 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] p-4 max-h-48 overflow-y-auto text-xs">
                                {audit.length === 0 ? (
                                    <div className="text-[#8C7A72]">No admin actions on this customer yet.</div>
                                ) : (
                                    audit.map((a) => (
                                        <div key={a.id} className="py-1.5 border-b border-[#E8DCCB] last:border-0">
                                            <span className="font-mono text-[#FF4F18] font-bold">{a.action}</span>
                                            <span className="ml-2">/ {a.tenant_slug}</span>
                                            <span className="ml-2 text-[#8C7A72]">{JSON.stringify(a.delta)}</span>
                                            {a.reason && <span className="ml-2 italic text-[#8C7A72]">"{a.reason}"</span>}
                                            <div className="text-[10px] text-[#8C7A72]">{new Date(a.created_at).toLocaleString()}</div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Café cards */}
                        {profile.cafes.length === 0 ? (
                            <div className="mt-6 rounded-2xl bg-[#F9F6F0] border border-dashed border-[#E8DCCB] p-8 text-center text-[#8C7A72]">
                                <Coffee className="h-8 w-8 mx-auto mb-2 opacity-40" strokeWidth={1.5} />
                                No café memberships. Customer hasn't ordered yet.
                            </div>
                        ) : (
                            <div className="mt-6 grid gap-3 md:grid-cols-2">
                                {profile.cafes.map((c) => (
                                    <CafeBoostCard
                                        key={c.slug}
                                        cafe={c}
                                        onFreeCoffee={(n) => grantFreeCoffee(c.slug, n)}
                                        onStamp={(d) => adjustStamps(c.slug, d)}
                                        onBeans={() => grantBeans(c.slug)}
                                    />
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

const CafeBoostCard = ({ cafe, onFreeCoffee, onStamp, onBeans }) => (
    <div className="rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] p-4" data-testid={`boost-cafe-${cafe.slug}`}>
        <div className="flex items-baseline justify-between">
            <div>
                <div className="font-display font-black">{cafe.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#8C7A72] font-semibold">
                    /{cafe.slug} · {cafe.plan || "starter"} · {cafe.subscription_status}
                </div>
            </div>
            <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">Lifetime</div>
                <div className="font-mono text-xs">${cafe.total_spent.toFixed(2)} · {cafe.order_count}x</div>
            </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-white p-2 border border-[#E8DCCB]">
                <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">Stamps</div>
                <div className="font-display font-black text-lg">
                    {cafe.stamps}<span className="text-xs text-[#8C7A72]">/{cafe.stamps_target}</span>
                </div>
            </div>
            <div className="rounded-xl bg-white p-2 border border-[#E8DCCB]">
                <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">Free coffees</div>
                <div className="font-display font-black text-lg text-[#FF4F18]">{cafe.free_coffees}</div>
            </div>
            <div className="rounded-xl bg-white p-2 border border-[#E8DCCB]">
                <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">Beans</div>
                <div className="font-display font-black text-lg">{cafe.beans_balance}</div>
            </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
            <button
                onClick={() => onFreeCoffee(1)}
                data-testid={`boost-free-1-${cafe.slug}`}
                className="flex-1 min-w-[110px] py-2 px-3 rounded-full bg-[#FF4F18] text-white text-xs font-display font-bold active:scale-95 flex items-center justify-center gap-1"
            >
                <Gift className="h-3 w-3" strokeWidth={2.5} />
                +1 free coffee
            </button>
            <button
                onClick={() => onFreeCoffee(3)}
                data-testid={`boost-free-3-${cafe.slug}`}
                className="py-2 px-3 rounded-full bg-white border-2 border-[#FF4F18] text-[#FF4F18] text-xs font-display font-bold active:scale-95"
            >
                +3
            </button>
            <button
                onClick={() => onStamp(1)}
                data-testid={`boost-stamp-plus-${cafe.slug}`}
                title="+1 stamp"
                className="py-2 px-2 rounded-full bg-white border border-[#E8DCCB] text-[#2C1A12] active:scale-95 flex items-center gap-1"
            >
                <Star className="h-3 w-3" strokeWidth={2.5} />
                <Plus className="h-3 w-3" strokeWidth={2.5} />
            </button>
            <button
                onClick={() => onStamp(-1)}
                data-testid={`boost-stamp-minus-${cafe.slug}`}
                title="-1 stamp"
                className="py-2 px-2 rounded-full bg-white border border-[#E8DCCB] text-[#2C1A12] active:scale-95 flex items-center gap-1"
            >
                <Star className="h-3 w-3" strokeWidth={2.5} />
                <Minus className="h-3 w-3" strokeWidth={2.5} />
            </button>
            <button
                onClick={onBeans}
                data-testid={`boost-beans-${cafe.slug}`}
                className="py-2 px-3 rounded-full bg-white border border-[#E8DCCB] text-[#2C1A12] text-xs font-display font-bold active:scale-95 flex items-center gap-1"
            >
                <Zap className="h-3 w-3" strokeWidth={2.5} />
                Grant Beans…
            </button>
        </div>
    </div>
);

const BulkBoost = () => {
    const [tenants, setTenants] = useState([]);
    const [slug, setSlug] = useState("");
    const [count, setCount] = useState(1);
    const [reason, setReason] = useState("Launch party");
    const [running, setRunning] = useState(false);
    const [confirm, setConfirm] = useState(false);

    useEffect(() => {
        superApi.get("/super/tenants").then((r) => setTenants(r.data.tenants || [])).catch(() => {});
    }, []);

    const fire = async () => {
        if (!slug) return;
        setRunning(true);
        try {
            const r = await superApi.post(`/super/tenants/${slug}/bulk-free-coffee`, {
                count, reason,
            });
            toast.success(
                `+${count} free coffee${count > 1 ? "s" : ""} to ${r.data.customers_touched} customer${r.data.customers_touched === 1 ? "" : "s"}`,
            );
            setConfirm(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk grant failed");
        } finally {
            setRunning(false);
        }
    };

    const cafe = tenants.find((t) => t.slug === slug);

    return (
        <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#FF4F18] to-[#E43F0A] text-white p-5" data-testid="bulk-boost">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold">
                <Users className="h-3.5 w-3.5" strokeWidth={2.5} />
                Bulk gift — every customer at one café
            </div>
            <div className="mt-1 font-display font-black text-lg">
                Launch parties · apology gifts · seasonal promos
            </div>

            <div className="mt-4 grid gap-2 md:grid-cols-[1fr_100px_1.5fr_auto] items-stretch">
                <select
                    data-testid="bulk-cafe-select"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    className="px-3 py-2.5 rounded-xl bg-white text-[#2C1A12] text-sm font-display font-bold outline-none"
                >
                    <option value="">Pick a café…</option>
                    {tenants.map((t) => (
                        <option key={t.slug} value={t.slug}>
                            {t.name} — /{t.slug}
                        </option>
                    ))}
                </select>
                <select
                    data-testid="bulk-count-select"
                    value={count}
                    onChange={(e) => setCount(parseInt(e.target.value, 10))}
                    className="px-3 py-2.5 rounded-xl bg-white text-[#2C1A12] text-sm font-display font-bold outline-none"
                >
                    {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>+{n} each</option>
                    ))}
                </select>
                <input
                    data-testid="bulk-reason-input"
                    type="text"
                    placeholder="Reason (audit-logged)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="px-3 py-2.5 rounded-xl bg-white text-[#2C1A12] text-sm outline-none"
                    maxLength={280}
                />
                <button
                    onClick={() => setConfirm(true)}
                    disabled={!slug}
                    data-testid="bulk-fire-btn"
                    className="px-5 py-2.5 rounded-xl bg-[#2C1A12] text-white font-display font-bold text-sm disabled:opacity-40 active:scale-95"
                >
                    Fire
                </button>
            </div>

            {/* Confirm modal */}
            <AnimatePresence>
                {confirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-6"
                        data-testid="bulk-confirm-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="w-full max-w-md rounded-3xl bg-white p-6 text-[#2C1A12]"
                        >
                            <div className="font-display font-black text-2xl">Confirm bulk grant</div>
                            <p className="mt-3 text-sm text-[#8C7A72]">
                                About to grant <span className="font-bold text-[#2C1A12]">+{count} free coffee{count > 1 ? "s" : ""}</span> to
                                {" "}<span className="font-bold text-[#2C1A12]">every customer</span> at
                                {" "}<span className="font-bold text-[#FF4F18]">{cafe?.name || slug}</span>.
                            </p>
                            <p className="mt-2 text-xs text-[#8C7A72]">
                                Reason: <span className="italic">"{reason}"</span> · This action is audit-logged per customer.
                            </p>
                            <div className="mt-5 flex gap-2">
                                <button
                                    onClick={() => setConfirm(false)}
                                    className="flex-1 py-3 rounded-full bg-white border-2 border-[#E8DCCB] font-display font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={fire}
                                    disabled={running}
                                    data-testid="bulk-confirm-btn"
                                    className="flex-1 py-3 rounded-full bg-[#FF4F18] text-white font-display font-bold disabled:opacity-60 active:scale-95"
                                >
                                    {running ? "Granting…" : "Yes, grant"}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CustomerBoost;